import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { selectOrgStructure } from "../../../../../store/orgStructure/repository/selector";
import { CourseFilters } from "./types";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { selectCourse } from "../../../../../store/course/repository/selector";
import { getCourseList } from "../../../../../store/course/useCases/getCourseList/action";
import { updateOrgStructure } from "../../../../../store/orgStructure/useCases/updateOrgStructure/action";
import toast from "react-hot-toast";
import { tokens } from "../../../../../locales/tokens";
import { updateCourse } from "../../../../../store/course/useCases/updateCourse/action";
import { deleteCourse } from "../../../../../store/course/useCases/deleteCourse/action";
import { TIMESTAMP_DAY } from "../../../../../config";

export const useCourseList = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const findStatusLabel = (value: string) => {
    if (value === "Available") return t(tokens.documents.publish);
    if (value === "Hidden") return t(tokens.documents.notPublish);
    return "";
  };
  const initialFilters: CourseFilters = {
    page: Number(searchParams.get("page")) || 1,
    rowsPerPage: Number(searchParams.get("rowsPerPage")) || 30,
    search: searchParams.get("search") || "",
    tab:
      (searchParams.get("tab") as unknown as CourseFilters["tab"]) ||
      ("Active" as CourseFilters["tab"]),
    status: searchParams.get("status")
      ? {
          value: searchParams.get("status"),
          label: findStatusLabel(searchParams.get("status") as string),
        }
      : null,
    initiator: searchParams.get("initiator")
      ? {
          value: searchParams.get("initiator"),
          label: searchParams.get("initiatorLabel") || "",
        }
      : null,
    date: searchParams.get("date") ? Number(searchParams.get("date")) : null,
  };

  const [filters, updateFilters] = useState<CourseFilters>(initialFilters);
  const [searchText, setSearchText] = useState(filters.search);

  const { orgStructure } = useSelector(selectOrgStructure);
  const {
    courseList,
    courseCount,
    isLoading,
    updateCourseError,
    deleteCourseError,
    getCourseListError,
  } = useSelector(selectCourse);
  const [pageAmount, setPageAmount] = useState(0);
  const [updateClick, setUpdateClick] = useState(false);
  const [deleteClick, setDeleteClick] = useState(false);
  const [isProccessCourse, setProcessCourse] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const [isOpen, setOpen] = useState(false);
  const [currentCourse, setCurrentCourse] = useState<any>(null);

  useEffect(() => {
    const params: Record<string, string> = {
      page: filters.page.toString(),
      rowsPerPage: filters.rowsPerPage.toString(),
      search: filters.search || "",
      tab: filters.tab,
    };
    if (filters.status) params.status = filters.status.value;
    if (filters.initiator) {
      params.initiator = filters.initiator.value;
      if (filters.initiator.label)
        params.initiatorLabel = filters.initiator.label;
    }
    if (filters.date) params.date = filters.date.toString();

    setSearchParams(params);
  }, [filters]);

  useEffect(() => {
    dispatch(getCourseList(combineRequestBody()));
    setProcessCourse(true);
  }, [filters]);

  useEffect(() => {
    if (courseCount) {
      setPageAmount(Math.ceil(courseCount / filters.rowsPerPage));
    }
  }, [courseCount]);

  useEffect(() => {
    dispatch(updateOrgStructure());
  }, []);

  useEffect(() => {
    if (!isLoading && updateClick) {
      if (updateCourseError) toast.error(updateCourseError);
      else {
        toast.success(t(tokens.course.message.courseUpdated));
        dispatch(getCourseList(combineRequestBody()));
      }
      setUpdateClick(false);
    }
  }, [updateCourseError, isLoading]);

  useEffect(() => {
    if (!isLoading && deleteClick) {
      if (deleteCourseError) toast.error(deleteCourseError);
      else {
        filters.tab === "Active"
          ? toast.success(t(tokens.course.message.courseRemoved))
          : toast.success(t(tokens.course.message.courseRemovedHard));
        dispatch(getCourseList(combineRequestBody()));
      }
      setDeleteClick(false);
    }
  }, [deleteCourseError, isLoading]);

  useEffect(() => {
    if (!isLoading && isProccessCourse) {
      if (getCourseListError) toast.error(getCourseListError);
      setTimeout(() => setProcessCourse(false), 400);
    }
  }, [getCourseListError, isLoading]);

  const handleSearchCourseText = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
    debouncedHandleCourseSeacrh(e.target.value);
  };

  const onSearchCourse = (value: string) => {
    const query = value ? value : "";
    if (query.length !== 1) {
      updateFilters((prevState) => ({ ...prevState, search: query, page: 1 }));
      scrollTopList();
    }
  };

  const { debouncedWrapper: debouncedHandleCourseSeacrh } = useDebounce(
    onSearchCourse,
    1000
  );

  const onSelectInitiator = (selectedKeysValue: string, node: any) => {
    if ("children" in node) return;
    updateFilters((prevState) => ({ ...prevState, page: 1, initiator: node }));
    scrollTopList();
  };

  const onChangeInitiator = () => {
    if (filters.initiator) {
      updateFilters((prevState) => ({
        ...prevState,
        page: 1,
        initiator: null,
      }));
    }
  };

  const resetFilters = useCallback((): void => {
    updateFilters((prevState) => ({
      ...prevState,
      page: 1,
      rowsPerPage: 30,
      status: null,
      initiator: null,
      date: null,
      search: "",
    }));
    scrollTopList();
  }, [updateFilters]);

  const handlePageChange = useCallback(
    (event: ChangeEvent<unknown>, page: number): void => {
      updateFilters((prevState) => ({ ...prevState, page }));
      scrollTopList();
    },
    [updateFilters]
  );

  const handleDateChange = useCallback(
    (date: Date | null): void => {
      const newDate = date?.valueOf() || null;
      updateFilters((prevState) => ({ ...prevState, page: 1, date: newDate }));
      scrollTopList();
    },
    [updateFilters]
  );

  const changeStatus = useCallback(
    (status: any) => {
      updateFilters((prevState) => ({ ...prevState, page: 1, status }));
      scrollTopList();
    },
    [updateFilters]
  );

  const handleTabsChange = (event: ChangeEvent<{}>, tab: string): void => {
    updateFilters((prev) => ({
      ...prev,
      tab: tab as CourseFilters["tab"],
      page: 1,
    }));
    scrollTopList();
  };

  const handleStatus = (id: number, status: string) => {
    const newStatus = status === "Hidden" ? "Available" : "Hidden";
    dispatch(updateCourse({ body: { status: newStatus }, id }));
    setUpdateClick(true);
  };

  const handleDelete = (id: number, mode?: string) => {
    dispatch(deleteCourse(mode ? { id, mode } : { id }));
    setDeleteClick(true);
  };

  const handleUploadReport = (id: number, name: string) => {
    setOpen(true);
    setCurrentCourse({ id, name });
  };

  const onCloseModal = () => {
    setOpen(false);
    setCurrentCourse(null);
  };

  const combineRequestBody = () => {
    const body: any = { page: filters.page, size: filters.rowsPerPage };
    if (filters.search) body.search = filters.search;
    if (filters.initiator) body.initiatorId = filters.initiator.value;
    if (filters.status) body.status = filters.status.value;
    if (filters.date)
      body.date = new Date(filters.date + TIMESTAMP_DAY).toISOString();
    if (filters.tab !== "Active") body.deleted = "true";
    return body;
  };

  const scrollTopList = () => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  };

  return {
    searchText,
    handleSearchCourseText,
    pageAmount,
    resetFilters,
    handlePageChange,
    filters,
    orgStructure,
    changeStatus,
    handleDateChange,
    isOpen,
    onCloseModal,
    onSelectInitiator,
    onChangeInitiator,
    handleTabsChange,
    handleStatus,
    handleDelete,
    handleUploadReport,
    courseList,
    isProccessCourse,
    scrollRef,
    currentCourse,
  };
};
